import React, { useState, useEffect, useCallback } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useStoreDispatch, useStoreSelector } from "../../../../../hooks/StoreHooks";
import { getCurrentUser } from "../../../../core/common_functions";
import { CategoryCode } from "../../../../models/category";
import Loading from "../../../../../components/Loading/loading";
import { useOptionsService } from "../../../../services/options_service";
import { useColorsService } from "../../../../services/colors_service";
import { setFioriere, usePergolaConfigurationBuilderActions } from "../../../../../store/reducers/pergola_configuration_builder";
import { AspectPergola, DimensionPergola, IlluminationType, LatoType, MuroPergola,Fioriera, PergolaConfiguration, Pilastri,Fioriere, Tubolare } from "../../../../models/pergola_configuration";
import { useConfigurationsServicePergola } from "../../../../services/pergola_service";
import { LatoIlluminazioneLed } from "./components/pergola-draw/Pergola";

export const PergolaConfigurationLoadPage = () => {
    const { id } = useParams<{ id: string }>();
    const navigate = useNavigate();
    const dispatcher = useStoreDispatch();
    const { base } = useStoreSelector(store => store);
    const currentUser = getCurrentUser(base);

    const { setStepVisible, setStepActive, setConfiguration, setPergolaSides,resetSides,resetBuilder,setLeds,setMuro,setPilastri,setTubolari } = usePergolaConfigurationBuilderActions();
    const pergolaService = useConfigurationsServicePergola();
    const optionsService = useOptionsService();
    const colorsService = useColorsService();

    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    function parsePilastri(raw: string | any): Pilastri[] {
        if (!raw) return [];
        let data: any[] = [];

        if (typeof raw === "string") {
            try {
                data = JSON.parse(raw);
            } catch {
                console.warn("Errore nel parsing dei pilastri");
                return [];
            }
        } else if (Array.isArray(raw)) {
            data = raw;
        } else {
            data = Object.values(raw);
        }

        // filtro e tipizzo correttamente
        return data.filter(
            (p): p is Pilastri =>
                typeof p.altezza === "number" &&
                ["H1", "H2", "H3", "H4"].includes(p.direzione)
        );
    }
    const handleStandardCardClick = () => {
        dispatcher(setStepVisible({ stepSlug: "dimensioni", isVisible: true }));
        dispatcher(setStepVisible({ stepSlug: "aspetto", isVisible: true }));
        dispatcher(setStepVisible({ stepSlug: "chiusure", isVisible: true }));
        dispatcher(setStepVisible({ stepSlug: "illuminazioni", isVisible: true }));
        dispatcher(setStepVisible({ stepSlug: "estetica", isVisible: true }));
        dispatcher(setStepVisible({ stepSlug: "fioriere", isVisible: true }));
        dispatcher(setStepVisible({ stepSlug: "accessori-vari", isVisible: true }));
        dispatcher(setStepVisible({ stepSlug: "condizioni", isVisible: true }));
        dispatcher(setStepActive({ stepSlug: "dimensioni", isActive: true }));
        navigate(`/pergola-configurations/${id}/dimensioni`);
    };

    const handleFuoriSquadroCardClick = () => {
        dispatcher(setStepVisible({ stepSlug: "condizioni", isVisible: true }));
        navigate(`/pergola-configurations/${id}/rilievo`);
    };

    const loadConfiguration = useCallback(async (configurationId: number) => {
    setLoading(true);
    setError(null);
    dispatcher(resetBuilder());
    try {
        const PergolaConfiguration = await pergolaService.loadDoorConfiguration(currentUser, configurationId);
        const options = await optionsService.findByCategory(currentUser, PergolaConfiguration.categoryId);
        const colors = await colorsService.findByCategory(currentUser, PergolaConfiguration.categoryId);
        dispatcher(setPergolaSides({ sides: [] }));
        dispatcher(setLeds([]));
        let tipo_pergolaa: string | undefined;
        try {
            tipo_pergolaa = await pergolaService.getForma(currentUser, configurationId);
        } catch (formaError) {
            console.warn("Errore nel caricamento forma pergola, imposto valore di default 'FuoriSquadro':", formaError);
            tipo_pergolaa = "FuoriSquadro";
        }

        let chiusure: any[] = [];
        let illuminazione: LatoIlluminazioneLed[] = [];
        let tubolari: Tubolare[] = [];
        let muri: MuroPergola[] = [];
        let pilastri: Pilastri[] = [];
        let fioriere: Fioriere[] = [];
        if (tipo_pergolaa === "Standard") {
            try {
                const rawChiusure = await pergolaService.getChiusure(currentUser, configurationId);
                chiusure = Array.isArray(rawChiusure)
                    ? rawChiusure
                    : rawChiusure
                    ? [rawChiusure]
                    : [];
            } catch (chiusureError) {
                console.warn("Errore nel caricamento chiusure", chiusureError);
                chiusure = [];
            }
            try {
                const rawPilastri = await pergolaService.getPilastri(currentUser, configurationId);
                pilastri = parsePilastri(rawPilastri);
            } catch (pilastriError) {
                console.warn("Errore nel caricamento dei pilastri", pilastriError);
                pilastri = [];
            }
            try {
                const rawMuri = await pergolaService.getMuri(currentUser, configurationId);

                if (rawMuri === null || rawMuri === undefined) {
                    muri = [];
                } else if (typeof rawMuri === "string") {
                    muri = JSON.parse(rawMuri);
                } else if (Array.isArray(rawMuri)) {
                    muri = rawMuri;
                } else {
                    muri = Object.values(rawMuri);
                }
                muri = muri.filter(muro => 
                    ["Retro", "Fronte", "Destra", "Sinistra"].includes(muro.direzione) && 
                    muro.altezza > 0
                );

            } catch (muriError) {
                console.warn("Errore nel caricamento dei muri", muriError);
                muri = [];
            }
            try {
                const rawIlluminazione = await pergolaService.getIlluminazioni(currentUser, configurationId);
                if (rawIlluminazione === null) {
                    illuminazione = [];
                } else if (typeof rawIlluminazione === "string") {
                    illuminazione = JSON.parse(rawIlluminazione);
                } else if (Array.isArray(rawIlluminazione)) {
                    illuminazione = rawIlluminazione;
                } else {
                    illuminazione = Object.values(rawIlluminazione);
                }
                illuminazione = illuminazione.map(item => ({
                    direzione: item.direzione as "Retro" | "Fronte" | "Destra" | "Sinistra",
                    tipo: item.tipo in IlluminationType ? item.tipo as IlluminationType : IlluminationType.nessuno
                }));
            } catch (illuminazioneError) {
                console.warn("Errore nel caricamento illuminazioni", illuminazioneError);
                illuminazione = [];
            }
            
             try {
                const rawTuboalri = await pergolaService.getTubolari(currentUser, configurationId);
                if (rawTuboalri === null) {
                    tubolari = [];
                } else if (typeof rawTuboalri === "string") {
                    tubolari = JSON.parse(rawTuboalri);
                } else if (Array.isArray(rawTuboalri)) {
                    tubolari = rawTuboalri;
                } else {
                    tubolari = Object.values(rawTuboalri);
                }
                tubolari = tubolari.map(item => ({
                    direzione: item.direzione as "Retro" | "Fronte" | "Destra" | "Sinistra" | "Retro2" | "Fronte2" | "Destra2" | "Sinistra2",
                    numero: item.numero
                }));
            } catch (TubolariError) {
                console.warn("Errore nel caricamento dei tuboalri", TubolariError);
                illuminazione = [];
            }
        } else {
        }
        try {
            const rawFioriere = await pergolaService.getFioriere(currentUser, configurationId);
            if (rawFioriere === null) {
                fioriere = [];
            } else if (typeof rawFioriere === "string") {
                fioriere = JSON.parse(rawFioriere);
            } else if (Array.isArray(rawFioriere)) {
                fioriere = rawFioriere;
            } else {
                fioriere = Object.values(rawFioriere);
            }

            fioriere = fioriere.map(item => ({
                direzione: item.direzione as "Retro" | "Fronte" | "Destra" | "Sinistra",
                tipo: item.tipo in Fioriera
                    ? item.tipo as Fioriera
                    : Fioriera.nessuna
            }));
        } catch (fioriereError) {
            console.warn("Errore nel caricamento fioriere", fioriereError);
            fioriere = [];
        }
        dispatcher(setLeds(illuminazione));
        dispatcher(setMuro(muri));
        dispatcher(setPilastri(pilastri));
        dispatcher(setTubolari(tubolari));
        dispatcher(setFioriere(fioriere));
        if (PergolaConfiguration.categoryCode === CategoryCode.Pergola) {
            const dimension: DimensionPergola = {
                installazione: PergolaConfiguration.installazione ? PergolaConfiguration.installazione : 75,
                quantita: PergolaConfiguration.quantita,
                altezza:
                    PergolaConfiguration.altezza && PergolaConfiguration.altezza > 0
                        ? PergolaConfiguration.altezza
                        : 2000,
                profondita:
                    PergolaConfiguration.profondita && PergolaConfiguration.profondita > 0
                        ? PergolaConfiguration.profondita
                        : 2000,
                larghezza:
                    PergolaConfiguration.larghezza && PergolaConfiguration.larghezza > 0
                        ? PergolaConfiguration.larghezza
                        : 2000,
                lati:  PergolaConfiguration.lati && PergolaConfiguration.lati
                        ? PergolaConfiguration.lati
                        : []
            };
            console.log("dimensioni lati",dimension);
            const aspetto: AspectPergola = {
                colore_telaio: PergolaConfiguration.coloreInterno ? String(PergolaConfiguration.coloreInterno) : "9010",
                colore_lamelle: PergolaConfiguration.coloreEsterno ? String(PergolaConfiguration.coloreEsterno) : "9010"
            };

            const pergolaConfiguration: PergolaConfiguration = {
                categoryCode: PergolaConfiguration.categoryCode,
                categoryId: PergolaConfiguration.categoryId,
                configurationId: PergolaConfiguration.configurationId,
                dimensioni: dimension,
                aspetto: aspetto,
                illuminazioneLed: illuminazione,
                tipo_pergola: tipo_pergolaa || "",
            };

            dispatcher(setConfiguration({ configuration: pergolaConfiguration, options, colors }));

            // Gestione sicura delle chiusure
            if (tipo_pergolaa === "Standard" && chiusure.length > 0) {
                const obj = chiusure[0];
                if (!obj) {
                    console.warn("Nessuna chiusura trovata, imposto lati vuoti.");
                    dispatcher(setPergolaSides({ sides: [] }));
                } else {
                    type ChiusuraItem = {
                        tipoLato: LatoType | null;
                        segmento: number,
                        direzione: "Retro" | "Fronte" | "Destra" | "Sinistra" | "Unknown";
                    };

                    const values = Object.values(obj) as ChiusuraItem[];

                    const sides = values
                        .filter(l => l.tipoLato)
                        .map(l => ({
                            direzione: l.direzione as "Retro" | "Fronte" | "Destra" | "Sinistra",
                            segmento: l.segmento,
                            tipoLato: l.tipoLato!
                        }));

                    dispatcher(setPergolaSides({ sides }));
                }
                if (PergolaConfiguration.isFinito) {
                    navigate(`/pergola-configurations/${PergolaConfiguration.configurationId}/preventivo`);
                }
            } else {
                console.warn("Nessuna chiusura caricata o tipo_pergola non è 'Standard', imposto lati vuoti.");
                dispatcher(resetSides());
                dispatcher(setPergolaSides({ sides: [] }));
            }
            if (PergolaConfiguration.isFinito) {
                if (tipo_pergolaa !== "Standard") {
                    navigate(`/pergola-configurations/${PergolaConfiguration.configurationId}/rilievo_preventivo`);
                }else{
                    navigate(`/pergola-configurations/${pergolaConfiguration.configurationId}/preventivo`);
                }
            } else {
                if (tipo_pergolaa === "Standard") {
                    handleStandardCardClick();
                } else if (tipo_pergolaa === "FuoriSquadro") {
                    handleFuoriSquadroCardClick();
                } else {
                    navigate(`/pergola-configurations/${pergolaConfiguration.configurationId}/selezione`);
                }
            }
        } else {
            console.warn("Categoria non supportata per la conversione in pergola:", PergolaConfiguration.categoryCode);
            setError("Categoria non supportata");
        }
    } catch (e: any) {
        console.error("Errore nel caricamento configurazione:", e);
        const errorMessage = e?.message || e?.toString() || "Errore sconosciuto";
        setError(`Errore nel caricamento della configurazione: ${errorMessage}`);
    } finally {
        setLoading(false);
    }
}, [currentUser, dispatcher, navigate, setConfiguration, setPergolaSides, setLeds, setTubolari]);

    useEffect(() => {
        if (!id || isNaN(Number(id))) {
            navigate("/configurations");
            return;
        }

        loadConfiguration(Number(id));

        return () => {
            setLoading(false);
        };
    }, [id, loadConfiguration, navigate]);

    if (error) {
        return (
            <div style={{ padding: "20px", textAlign: "center", color: "red" }}>
                <p>{error}</p>
                <button onClick={() => navigate("/configurations")}>Torna alle configurazioni</button>
            </div>
        );
    }

    return loading ? <Loading /> : null;
};
